import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LogoIcon } from '../../../icons';
import { useAuth } from '../../../contexts/AuthContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
  isMissingSsoConfigError,
  requestPasswordRecovery,
} from '../../../api/authService';
import Button from '../../../components/styled_api/StyledButton';
import AuthSplitLayout from './AuthSplitLayout';
import CheckYourEmail from './CheckYourEmail';

interface FormValues {
  email: string;
  password: string;
}

type LoginStep = 'email' | 'password' | 'check-email';

const INCORRECT_CREDENTIALS_MESSAGE = 'Incorrect email address or password.';

// Validation schema depends on whether password has been requested yet
const getValidationSchema = (step: LoginStep) =>
  Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password:
      step === 'password'
        ? Yup.string().required('Password is required')
        : Yup.string(),
  });

const LoginPage: React.FC = () => {
  const { login, ssoLogin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // 'email': Continue probes SSO. 'password': email/password login. 'check-email': recover sent.
  const [step, setStep] = useState<LoginStep>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [recoveredEmail, setRecoveredEmail] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);
  const [focusedField, setFocusedField] = useState<'email' | 'password' | null>(null);

  const authError = searchParams.get('auth_error');
  const authErrorMessage = searchParams.get('auth_error_message');
  useEffect(() => {
    if (!authError) return;

    toast.error(
      authErrorMessage?.trim() ||
        'Sign-in failed. Please try again or contact your administrator.'
    );

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('auth_error');
        next.delete('auth_error_message');
        return next;
      },
      { replace: true }
    );
  }, [authError, authErrorMessage, setSearchParams]);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Email is required');
      return;
    }
    setIsRecovering(true);
    try {
      const result = await requestPasswordRecovery(trimmed);
      if (result.isSuccess) {
        setRecoveredEmail(trimmed);
        setLoginError(null);
        setStep('check-email');
      } else {
        toast.error(result.message || 'Failed to send password recovery email');
      }
    } finally {
      setIsRecovering(false);
    }
  };

  const onSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    try {
      if (step === 'email') {
        setLoginError(null);
        try {
          // Probe only: rethrow errors to this page; LoginPage stays mounted
          // (interactive auth does not toggle AuthContext isLoading).
          await ssoLogin(values.email, {
            shouldSuppressErrorToast: true,
          });
        } catch (err) {
          if (isMissingSsoConfigError(err)) {
            setStep('password');
            return;
          }
          const message = err instanceof Error ? err.message : String(err);
          toast.error(message || 'Unable to start SSO login.');
        }
        return;
      }

      // Password step (check-email renders CheckYourEmail and unmounts this form).
      setLoginError(null);
      try {
        await login(values.email, values.password, {
          shouldSuppressErrorToast: true,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);

        if (message.toLowerCase().includes('please use sso')) {
          try {
            await ssoLogin(values.email);
          } catch {
            return;
          }
          return;
        }

        setLoginError(INCORRECT_CREDENTIALS_MESSAGE);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'check-email') {
    return (
      <AuthSplitLayout>
        <CheckYourEmail
          email={recoveredEmail}
          onBack={() => setStep('password')}
        />
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src={LogoIcon}
          alt="MarketSmart logo"
          sx={{ width: 32, height: 32 }}
        />
        <Typography
          component="h1"
          variant="h5"
          textAlign="center"
          sx={{ lineHeight: '28px' }}
        >
          Welcome back
        </Typography>
      </Box>

      <Formik
        initialValues={{ email: recoveredEmail, password: '' }}
        validationSchema={getValidationSchema(step)}
        validateOnChange={false}
        validateOnBlur
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          submitCount,
          isSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => {
          // Avoid validation errors while user is typing
          const showFieldError = (
            field: 'email' | 'password',
            error: string | undefined,
            wasTouched: boolean | undefined,
            value: string
          ) =>
            Boolean(error) &&
            (submitCount > 0 ||
              (focusedField !== field && Boolean(wasTouched) && Boolean(value)));

          const showEmailError = showFieldError(
            'email',
            errors.email,
            touched.email,
            values.email
          );
          const showPasswordError = showFieldError(
            'password',
            errors.password,
            touched.password,
            values.password
          );

          return (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
          >
            {loginError && (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                {loginError}
              </Alert>
            )}

            <Field
              as={TextField}
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus={step === 'email'}
              error={showEmailError}
              helperText={showEmailError ? errors.email : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (loginError) {
                  setLoginError(null);
                }
                if (step === 'password') {
                  setStep('email');
                  setFieldValue('password', '');
                  setFieldTouched('password', false, false);
                }
                handleChange(e);
              }}
              onFocus={() => setFocusedField('email')}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                setFocusedField(null);
                handleBlur(e);
              }}
            />

            {step === 'password' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Field
                  as={TextField}
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  data-testid="password-input"
                  autoComplete="current-password"
                  autoFocus
                  error={showPasswordError}
                  helperText={showPasswordError ? errors.password : undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (loginError) {
                      setLoginError(null);
                    }
                    handleChange(e);
                  }}
                  onFocus={() => setFocusedField('password')}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    setFocusedField(null);
                    handleBlur(e);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handlePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Typography
                  component="button"
                  type="button"
                  disabled={isRecovering || isSubmitting}
                  onClick={() => void handleForgotPassword(values.email)}
                  sx={{
                    alignSelf: 'flex-end',
                    border: 0,
                    background: 'none',
                    padding: 0,
                    color: 'primary.main',
                    fontSize: 14,
                    lineHeight: '20px',
                    letterSpacing: '0.17px',
                    textDecoration: 'underline',
                    cursor: isRecovering ? 'default' : 'pointer',
                  }}
                >
                  {isRecovering ? 'Sending...' : 'Forgot your password?'}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                type="submit"
                className="button__primaryMain"
                width="100%"
                disabled={isSubmitting || isRecovering}
                label={ isSubmitting ? '' :
                  step === 'email'
                      ? 'Continue'
                      : 'Go to my dashboard'
                }
                sx={{ '&&': { height: 40, minHeight: 40 } }} // `&&` beats `.button` default styling
                aria-label={
                  step === 'email' ? 'Continue' : 'Go to my dashboard'
                }
              >
                {isSubmitting && (
                  <CircularProgress size={20} color="inherit" />
                )}
              </Button>
              <Typography
                textAlign="center"
                sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.17px' }}
              >
                New at MarketSmart?{' '}
                <Box
                  component="a"
                  href="https://imarketsmart.com/get-a-free-demo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    letterSpacing: '0.17px',
                  }}
                >
                  Sign up
                </Box>
              </Typography>
            </Box>
          </Form>
          );
        }}
      </Formik>
    </AuthSplitLayout>
  );
};

export default LoginPage;
